import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Building, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import '../styles/Builders.css';
import '../styles/DataTable.css';
import '../styles/Contacts.css';

interface Project {
  _id: string;
  name: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface UnitData {
  _id: string;
  unitNo: string;
  unitNoLegal: string;
  type: string;
  modelName: string;
  buildingType: string;
  bed: number;
  bath: number;
  den: number;
  status: string;
  floorMarketing: number;
  area: number;
  price: number;
}

const ReportsUnits: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [unitsData, setUnitsData] = useState<UnitData[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Filters
  const [buildingTypeFilter, setBuildingTypeFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [modelFilter, setModelFilter] = useState<string>('');
  const [bedFilter, setBedFilter] = useState<string>('');
  const [bathFilter, setBathFilter] = useState<string>('');
  const [denFilter, setDenFilter] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>(''); // Local state for immediate UI update

  // Available filter options (will be populated from data)
  const [buildingTypes, setBuildingTypes] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [bedOptions, setBedOptions] = useState<number[]>([]);
  const [bathOptions, setBathOptions] = useState<number[]>([]);
  const [denOptions, setDenOptions] = useState<number[]>([]);

  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Modal state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');

  // Report configuration state
  const [reportConfig, setReportConfig] = useState({
    unitDetails: {
      enabled: true,
      modelCollection: true,
      modelName: true,
      type: true,
      unitNo: true,
      beds: true,
      baths: true,
      den: true,
      area: true,
      outdoor: true
    },
    layoutDetails: {
      enabled: true,
      suiteLayout: { enabled: true, size: 'large' },
      floorLayout: { enabled: true, size: 'large' },
      electricalLayout: { enabled: true, size: 'large' },
      photos: { enabled: true, size: 'large' }
    },
    dealDetails: {
      enabled: true,
      purchasers: true,
      price: true,
      importantDates: true
    },
    extras: {
      enabled: true,
      selectedParking: true,
      selectedBicycle: true,
      selectedLocker: true
    },
    colorSelection: {
      enabled: true,
      colorCollection: true,
      colorCollectionPhotos: true,
      colorCollectionItems: true,
      packages: true,
      packagesPhotos: true,
      packagesItems: true,
      options: true,
      optionsPhotos: true,
      optionsItems: true
    }
  });

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const fetchProjects = useCallback(async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`);
      setProjects(response.data.projects || []);

      // Auto-select first project if available
      if (response.data.projects && response.data.projects.length > 0) {
        setSelectedProjectId(response.data.projects[0]._id);
      }
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const fetchUnits = useCallback(async () => {
    if (!selectedProjectId || !workspaceId) return;

    try {
      setLoadingReports(true);
      const params: any = {
        projectId: selectedProjectId,
        page: pagination.page,
        limit: pagination.limit
      };

      if (buildingTypeFilter) params.buildingType = buildingTypeFilter;
      if (typeFilter) params.type = typeFilter;
      if (modelFilter) params.modelName = modelFilter;
      if (bedFilter) params.bed = bedFilter;
      if (bathFilter) params.bath = bathFilter;
      if (denFilter) params.den = denFilter;
      if (searchText) params.search = searchText;

      const response = await api.get(`/workspaces/${workspaceId}/units`, {
        params
      });

      setUnitsData(response.data.units || []);
      setPagination(response.data.pagination);

      // Extract unique filter options from all units (for dropdowns)
      if (response.data.units && response.data.units.length > 0) {
        const units = response.data.units;

        const uniqueBuildingTypes = Array.from(new Set(units.map((u: UnitData) => u.buildingType).filter(Boolean)));
        const uniqueTypes = Array.from(new Set(units.map((u: UnitData) => u.type).filter(Boolean)));
        const uniqueModels = Array.from(new Set(units.map((u: UnitData) => u.modelName).filter(Boolean)));

        const bedNumbers: number[] = units.map((u: UnitData) => u.bed).filter((b: any): b is number => typeof b === 'number' && b !== null && b !== undefined);
        const uniqueBeds = (Array.from(new Set(bedNumbers)) as number[]).sort((a, b) => a - b);

        const bathNumbers: number[] = units.map((u: UnitData) => u.bath).filter((b: any): b is number => typeof b === 'number' && b !== null && b !== undefined);
        const uniqueBaths = (Array.from(new Set(bathNumbers)) as number[]).sort((a, b) => a - b);

        const denNumbers: number[] = units.map((u: UnitData) => u.den).filter((d: any): d is number => typeof d === 'number' && d !== null && d !== undefined);
        const uniqueDens = (Array.from(new Set(denNumbers)) as number[]).sort((a, b) => a - b);

        setBuildingTypes(uniqueBuildingTypes as string[]);
        setTypes(uniqueTypes as string[]);
        setModels(uniqueModels as string[]);
        setBedOptions(uniqueBeds);
        setBathOptions(uniqueBaths);
        setDenOptions(uniqueDens);
      }
    } catch (err: any) {
      console.error('Error fetching units:', err);
      toast.error('Failed to load units');
    } finally {
      setLoadingReports(false);
    }
  }, [workspaceId, selectedProjectId, buildingTypeFilter, typeFilter, modelFilter, bedFilter, bathFilter, denFilter, searchText, pagination.page, pagination.limit]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchText(searchInput);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchUnits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, selectedProjectId, buildingTypeFilter, typeFilter, modelFilter, bedFilter, bathFilter, denFilter, searchText, pagination.page, pagination.limit]);

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProjectId(e.target.value);
    clearFilters();
  };

  const clearFilters = () => {
    setBuildingTypeFilter('');
    setTypeFilter('');
    setModelFilter('');
    setBedFilter('');
    setBathFilter('');
    setDenFilter('');
    setSearchText('');
    setSearchInput('');
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ ...pagination, limit, page: 1 });
  };

  const handleGenerate = async (unitId: string) => {
    setSelectedUnitId(unitId);
    setShowPrintModal(true);

    // Load user settings for this workspace
    try {
      const response = await api.get(`/workspaces/${workspaceId}/user-settings`, {
        params: { settingType: 'UNIT_REPORT_CONFIG' }
      });
      if (response.data.success && response.data.settings) {
        setReportConfig(response.data.settings);
      }
    } catch (err: any) {
      // If settings don't exist, use default config (already set in state)
      console.log('No saved settings found, using defaults');
    }
  };

  const handleCloseModal = () => {
    setShowPrintModal(false);
    setSelectedUnitId('');
  };

  const handleGenerateReport = async () => {
    if (!selectedUnitId || !workspaceId) return;

    setLoadingReports(true);
    try {
      // Save user settings first
      await api.post(`/workspaces/${workspaceId}/user-settings`, {
        settingType: 'UNIT_REPORT_CONFIG',
        settings: reportConfig
      });

      toast.info('Generating report...');

      // Generate PDF report
      const response = await api.post(
        `/workspaces/${workspaceId}/units/${selectedUnitId}/generate-report`,
        { reportConfig },
        { responseType: 'blob' }
      );

      // Create a download link for the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Unit_Report_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Report generated successfully');
      handleCloseModal();
    } catch (err: any) {
      console.error('Error generating report:', err);
      toast.error('Failed to generate report');
    } finally {
      setLoadingReports(false);
    }
  };

  const toggleSection = (section: string) => {
    setReportConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section as keyof typeof prev],
        enabled: !prev[section as keyof typeof prev].enabled
      }
    }));
  };

  const toggleField = (section: string, field: string) => {
    setReportConfig(prev => {
      const sectionData = prev[section as keyof typeof prev] as any;
      return {
        ...prev,
        [section]: {
          ...sectionData,
          [field]: !sectionData[field]
        }
      };
    });
  };

  const toggleLayoutSize = (layout: string, size: string) => {
    setReportConfig(prev => {
      const layoutItem = prev.layoutDetails[layout as keyof typeof prev.layoutDetails];
      const currentLayout = typeof layoutItem === 'object' && layoutItem !== null ? layoutItem : { enabled: true, size: 'large' };
      return {
        ...prev,
        layoutDetails: {
          ...prev.layoutDetails,
          [layout]: {
            ...currentLayout,
            size
          }
        }
      };
    });
  };

  const selectAll = () => {
    setReportConfig({
      unitDetails: {
        enabled: true,
        modelCollection: true,
        modelName: true,
        type: true,
        unitNo: true,
        beds: true,
        baths: true,
        den: true,
        area: true,
        outdoor: true
      },
      layoutDetails: {
        enabled: true,
        suiteLayout: { enabled: true, size: 'large' },
        floorLayout: { enabled: true, size: 'large' },
        electricalLayout: { enabled: true, size: 'large' },
        photos: { enabled: true, size: 'large' }
      },
      dealDetails: {
        enabled: true,
        purchasers: true,
        price: true,
        importantDates: true
      },
      extras: {
        enabled: true,
        selectedParking: true,
        selectedBicycle: true,
        selectedLocker: true
      },
      colorSelection: {
        enabled: true,
        colorCollection: true,
        colorCollectionPhotos: true,
        colorCollectionItems: true,
        packages: true,
        packagesPhotos: true,
        packagesItems: true,
        options: true,
        optionsPhotos: true,
        optionsItems: true
      }
    });
  };

  if (loading) {
    return (
      <AppLayout onBack={handleBackToWorkspaces}>
        <div className="builders-container">
          <div className="builders-loading">
            <div className="builders-loading-spinner" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (projects.length === 0) {
    return (
      <AppLayout onBack={handleBackToWorkspaces}>
        <div className="builders-container">
          <div className="builders-section-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h1 className="builders-section-title">Units Reports</h1>
              <p className="builders-section-subtitle">
                Units reports and analytics
              </p>
            </div>
          </div>
          <div className="builders-empty-state">
            <Building size={48} color="#A89088" style={{ marginBottom: '16px' }} />
            <p>No projects found. Please create a project first.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces}>
      <div className="builders-container">
        {/* Page Header with Project Dropdown */}
        <div className="builders-section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h1 className="builders-section-title">Units Reports</h1>
            <p className="builders-section-subtitle">
              Units reports and analytics
            </p>
          </div>
          <div style={{ marginTop: '4px' }}>
            <select
              className="form-input"
              value={selectedProjectId}
              onChange={handleProjectChange}
              disabled={loading || projects.length === 0}
              style={{ minWidth: '280px' }}
            >
              <option value="">Select Project</option>
              {projects.map(project => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filters Section */}
        {selectedProjectId && (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* First Row of Filters */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <select
                  className="form-input"
                  value={buildingTypeFilter}
                  onChange={(e) => {
                    setBuildingTypeFilter(e.target.value);
                    setPagination({ ...pagination, page: 1 });
                  }}
                  style={{ flex: '1', minWidth: '200px' }}
                >
                  <option value="">All Building Type</option>
                  {buildingTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>

                <select
                  className="form-input"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPagination({ ...pagination, page: 1 });
                  }}
                  style={{ flex: '1', minWidth: '200px' }}
                >
                  <option value="">All Type</option>
                  {types.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>

                <select
                  className="form-input"
                  value={modelFilter}
                  onChange={(e) => {
                    setModelFilter(e.target.value);
                    setPagination({ ...pagination, page: 1 });
                  }}
                  style={{ flex: '1', minWidth: '200px' }}
                >
                  <option value="">All Model</option>
                  {models.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>

                <select
                  className="form-input"
                  value={bedFilter}
                  onChange={(e) => {
                    setBedFilter(e.target.value);
                    setPagination({ ...pagination, page: 1 });
                  }}
                  style={{ flex: '1', minWidth: '200px' }}
                >
                  <option value="">All Beds</option>
                  {bedOptions.map(bed => (
                    <option key={bed} value={bed}>{bed} Bed{bed !== 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              {/* Second Row of Filters */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <select
                  className="form-input"
                  value={bathFilter}
                  onChange={(e) => {
                    setBathFilter(e.target.value);
                    setPagination({ ...pagination, page: 1 });
                  }}
                  style={{ flex: '1', minWidth: '200px' }}
                >
                  <option value="">All Baths</option>
                  {bathOptions.map(bath => (
                    <option key={bath} value={bath}>{bath} Bath{bath !== 1 ? 's' : ''}</option>
                  ))}
                </select>

                <select
                  className="form-input"
                  value={denFilter}
                  onChange={(e) => {
                    setDenFilter(e.target.value);
                    setPagination({ ...pagination, page: 1 });
                  }}
                  style={{ flex: '1', minWidth: '200px' }}
                >
                  <option value="">All Den</option>
                  {denOptions.map(den => (
                    <option key={den} value={den}>{den} Den{den !== 1 ? 's' : ''}</option>
                  ))}
                </select>

                <input
                  type="text"
                  className="form-input"
                  placeholder="Type unit no"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ flex: '1', minWidth: '200px' }}
                />

                <button
                  className="btn btn-secondary"
                  onClick={clearFilters}
                  style={{ minWidth: '100px' }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {loadingReports ? (
          <div className="builders-loading">
            <div className="builders-loading-spinner" />
          </div>
        ) : unitsData.length > 0 ? (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Table */}
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <div className="data-table-container">
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '50px', textAlign: 'center' }}>
                        <input type="checkbox" />
                      </th>
                      <th style={{ width: 'auto' }}>Unit No ▼</th>
                      <th style={{ width: '150px', textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unitsData.map((unit, index) => (
                      <tr key={unit._id} style={{
                        backgroundColor: index % 2 === 0 ? '#FFF' : '#F9F5F3'
                      }}>
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" />
                        </td>
                        <td className="data-table-primary-cell">{unit.unitNo}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleGenerate(unit._id)}
                            style={{
                              backgroundColor: '#10B981',
                              borderColor: '#10B981',
                              padding: '6px 20px'
                            }}
                          >
                            Generate
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination and Records Per Page */}
            <div className="contacts-pagination-controls">
              {/* Records Per Page Selector */}
              <div className="contacts-records-per-page">
                <span>Show</span>
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>records per page (Total: {pagination.total})</span>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="contacts-pagination-wrapper">
                  <Pagination
                    current={pagination.page}
                    total={pagination.pages}
                    onPageChange={handlePageChange}
                    maxWidth={400}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="builders-empty-state">
            <Building size={48} color="#A89088" style={{ marginBottom: '16px' }} />
            <p>No units available for this project</p>
          </div>
        )}

        {/* Print Unit Report Modal */}
        {showPrintModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#48182F' }}>
                  Print Unit Report
                </h2>
                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: '#6B7280'
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Body */}
              <div style={{
                padding: '24px',
                overflowY: 'auto',
                flex: 1
              }}>
                {/* Header with Select All */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <p style={{ margin: 0, color: '#6B7280', fontSize: '14px' }}>
                    Select the components to be included in the report
                  </p>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={selectAll}
                      style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#A89088' }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Select All</span>
                  </label>
                </div>

                {/* Table Headers */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '2px solid #E5E7EB'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                    Information Attribute
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                    Attribute Label
                  </div>
                </div>

                {/* Unit Details Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      checked={reportConfig.unitDetails.enabled}
                      onChange={() => toggleSection('unitDetails')}
                      style={{ marginTop: '3px', marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer', accentColor: '#A89088' }}
                    />
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#48182F' }}>Unit Details</span>
                  </label>

                  {reportConfig.unitDetails.enabled && (
                    <div style={{ marginLeft: '26px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { key: 'modelCollection', label: 'Model Collection' },
                        { key: 'modelName', label: 'Model Name' },
                        { key: 'type', label: 'Type' },
                        { key: 'unitNo', label: 'Unit #' },
                        { key: 'beds', label: 'Beds' },
                        { key: 'baths', label: 'Baths' },
                        { key: 'den', label: 'Den' },
                        { key: 'area', label: 'Area(Sq.Ft)' },
                        { key: 'outdoor', label: 'Outdoor(Sq.Ft)' }
                      ].map(item => (
                        <div key={item.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={reportConfig.unitDetails[item.key as keyof typeof reportConfig.unitDetails] as boolean}
                              onChange={() => toggleField('unitDetails', item.key)}
                              style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#A89088' }}
                            />
                            <span style={{ fontSize: '14px', color: '#374151' }}>{item.label}</span>
                          </label>
                          <input
                            type="text"
                            defaultValue={item.label}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Layout Details Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      checked={reportConfig.layoutDetails.enabled}
                      onChange={() => toggleSection('layoutDetails')}
                      style={{ marginTop: '3px', marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer', accentColor: '#A89088' }}
                    />
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#48182F' }}>Layout Details</span>
                  </label>

                  {reportConfig.layoutDetails.enabled && (
                    <div style={{ marginLeft: '26px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {[
                        { key: 'suiteLayout', label: 'Suite Layout' },
                        { key: 'floorLayout', label: 'Floor Layout' },
                        { key: 'electricalLayout', label: 'Electrical Layout' },
                        { key: 'photos', label: 'Photos' }
                      ].map(item => {
                        const layoutItem = reportConfig.layoutDetails[item.key as keyof typeof reportConfig.layoutDetails];
                        const isObject = typeof layoutItem === 'object' && layoutItem !== null && 'enabled' in layoutItem;
                        return (
                          <div key={item.key}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '8px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={isObject ? layoutItem.enabled : false}
                                  onChange={() => toggleField('layoutDetails', item.key)}
                                  style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#A89088' }}
                                />
                                <span style={{ fontSize: '14px', color: '#374151' }}>{item.label}</span>
                              </label>
                              <input
                                type="text"
                                defaultValue={item.label}
                                style={{
                                  padding: '8px 12px',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: '6px',
                                  fontSize: '14px'
                                }}
                              />
                            </div>
                            <div style={{ marginLeft: '24px', display: 'flex', gap: '16px' }}>
                              {['small', 'medium', 'large'].map(size => (
                                <label key={size} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                  <input
                                    type="radio"
                                    name={`${item.key}-size`}
                                    checked={isObject ? layoutItem.size === size : false}
                                    onChange={() => toggleLayoutSize(item.key, size)}
                                    style={{ marginRight: '6px', cursor: 'pointer', accentColor: '#A89088' }}
                                  />
                                  <span style={{ fontSize: '13px', color: '#6B7280', textTransform: 'capitalize' }}>{size}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Deal Details Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      checked={reportConfig.dealDetails.enabled}
                      onChange={() => toggleSection('dealDetails')}
                      style={{ marginTop: '3px', marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer', accentColor: '#A89088' }}
                    />
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#48182F' }}>Deal Details</span>
                  </label>

                  {reportConfig.dealDetails.enabled && (
                    <div style={{ marginLeft: '26px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { key: 'purchasers', label: 'Purchasers' },
                        { key: 'price', label: 'Price' },
                        { key: 'importantDates', label: 'Important Dates' }
                      ].map(item => (
                        <div key={item.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={reportConfig.dealDetails[item.key as keyof typeof reportConfig.dealDetails] as boolean}
                              onChange={() => toggleField('dealDetails', item.key)}
                              style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#A89088' }}
                            />
                            <span style={{ fontSize: '14px', color: '#374151' }}>{item.label}</span>
                          </label>
                          <input
                            type="text"
                            defaultValue={item.label}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Extras Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      checked={reportConfig.extras.enabled}
                      onChange={() => toggleSection('extras')}
                      style={{ marginTop: '3px', marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer', accentColor: '#A89088' }}
                    />
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#48182F' }}>Extras</span>
                  </label>

                  {reportConfig.extras.enabled && (
                    <div style={{ marginLeft: '26px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { key: 'selectedParking', label: 'Selected Parking' },
                        { key: 'selectedBicycle', label: 'Selected Bicycle' },
                        { key: 'selectedLocker', label: 'Selected Locker' }
                      ].map(item => (
                        <div key={item.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={reportConfig.extras[item.key as keyof typeof reportConfig.extras] as boolean}
                              onChange={() => toggleField('extras', item.key)}
                              style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#A89088' }}
                            />
                            <span style={{ fontSize: '14px', color: '#374151' }}>{item.label}</span>
                          </label>
                          <input
                            type="text"
                            defaultValue={item.label}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Color Selection Section */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      checked={reportConfig.colorSelection.enabled}
                      onChange={() => toggleSection('colorSelection')}
                      style={{ marginTop: '3px', marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer', accentColor: '#A89088' }}
                    />
                    <span style={{ fontSize: '15px', fontWeight: 600, color: '#48182F' }}>Color Selection</span>
                  </label>

                  {reportConfig.colorSelection.enabled && (
                    <div style={{ marginLeft: '26px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { key: 'colorCollection', label: 'Color Collection' },
                        { key: 'colorCollectionPhotos', label: 'Color Collection Photos', displayLabel: 'Photos' },
                        { key: 'colorCollectionItems', label: 'Color Collection Items', displayLabel: 'Included Items' },
                        { key: 'packages', label: 'Packages' },
                        { key: 'packagesPhotos', label: 'Packages Photos', displayLabel: 'Photos' },
                        { key: 'packagesItems', label: 'Packages Items', displayLabel: 'Included Items' },
                        { key: 'options', label: 'Options' },
                        { key: 'optionsPhotos', label: 'Options Photos', displayLabel: 'Photos' },
                        { key: 'optionsItems', label: 'Options Items', displayLabel: 'Included Items' }
                      ].map(item => (
                        <div key={item.key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={reportConfig.colorSelection[item.key as keyof typeof reportConfig.colorSelection] as boolean}
                              onChange={() => toggleField('colorSelection', item.key)}
                              style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#A89088' }}
                            />
                            <span style={{ fontSize: '14px', color: '#374151' }}>{item.label}</span>
                          </label>
                          <input
                            type="text"
                            defaultValue={item.displayLabel || item.label}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button
                  onClick={handleCloseModal}
                  className="btn btn-secondary"
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px'
                  }}
                >
                  Close
                </button>
                <button
                  onClick={handleGenerateReport}
                  disabled={loadingReports}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    backgroundColor: loadingReports ? '#6B7280' : '#1F2937',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: loadingReports ? 'not-allowed' : 'pointer',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: loadingReports ? 0.7 : 1
                  }}
                >
                  {loadingReports && (
                    <svg
                      className="animate-spin"
                      style={{
                        width: '16px',
                        height: '16px',
                        animation: 'spin 1s linear infinite'
                      }}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        style={{ opacity: 0.25 }}
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        style={{ opacity: 0.75 }}
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {loadingReports ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ReportsUnits;
